import React, { useState, useMemo } from "react"
import ContentBrowser from "aldoo-ra/CMS/browsers/content-browser"
import DropdownSelect from "aldoo-ra/DropdownSelect"
import Typography from "aldoo-ra/Typography"
import Tooltip from "aldoo-ra/Tooltip"
import { showNotification } from "aldoo-ra/Notification"
import TypeResolver from "aldoo-ra/CMS/type-resolver"
import { TagIcon, HashtagIcon, CalendarDaysIcon, DocumentTextIcon } from "@heroicons/react/24/outline"

export default function MailTemplateBrowser() {
  const [selectedCategory, setSelectedCategory] = useState([])
  const [refreshKey, setRefreshKey] = useState(0)

  const { getContentType } = TypeResolver()

  // Get MailTemplate type definition and extract category options
  const mailTemplateType = getContentType("MailTemplate")
  const categoryField = mailTemplateType?.fields?.find(f => f.id === "category")

  // Extract category options from the type definition
  const categoryOptionsFromType = useMemo(() => {
    if (!categoryField?.options) return []

    return categoryField.options.map(option => {
      if (typeof option === "string") {
        return { value: option, label: option.charAt(0).toUpperCase() + option.slice(1) }
      }
      return { value: option.value, label: option.name || option.label || option.value }
    })
  }, [categoryField])

  // Category display mapping - derive from options
  const categoryDisplayMap = useMemo(() => {
    const colorMap = {
      "system": { badge: "bg-admin_info text-white" },
      "user": { badge: "bg-admin_success text-white" },
      "newsletter": { badge: "bg-admin_warning text-white" }
    }

    const map = {}
    categoryOptionsFromType.forEach(option => {
      map[option.value] = {
        name: option.label,
        badge: colorMap[option.value]?.badge || "bg-admin_neutral_3 text-white"
      }
    })
    return map
  }, [categoryOptionsFromType])

  // Copy to clipboard function
  const copyToClipboard = async (text, label = "ID") => {
    try {
      await navigator.clipboard.writeText(text)
      showNotification({
        type: "success",
        message: `${label} copied to clipboard!`,
        duration: 2000
      })
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement("textarea")
      textArea.value = text
      document.body.appendChild(textArea)
      textArea.select()
      document.execCommand('copy')
      document.body.removeChild(textArea)

      showNotification({
        type: "success",
        message: `${label} copied to clipboard!`,
        duration: 2000
      })
    }
  }

  // Data model for MailTemplate-specific filter suggestions
  const mailTemplateDataModel = useMemo(() => [
    {
      key: "content.name",
      label: "Template Name",
      type: "text",
      icon: DocumentTextIcon,
      description: "Filter by template name"
    },
    {
      key: "content.id",
      label: "Template ID",
      type: "text",
      icon: HashtagIcon,
      description: "Filter by template identifier"
    },
    {
      key: "content.category",
      label: "Category",
      type: "select",
      icon: TagIcon,
      description: "Filter by template category",
      options: categoryField?.options || ["system", "user", "newsletter"]
    },
    {
      key: "content.description",
      label: "Description",
      type: "text",
      icon: DocumentTextIcon,
      description: "Filter by template description"
    },
    {
      key: "createdAt",
      label: "Created Date",
      type: "date",
      icon: CalendarDaysIcon,
      description: "Filter by creation date"
    },
    {
      key: "updatedAt",
      label: "Updated Date",
      type: "date",
      icon: CalendarDaysIcon,
      description: "Filter by last update date"
    }
  ], [categoryField])

  // Build dynamic filters based on category selection
  const dynamicFilters = useMemo(() => {
    // Always filter for MailTemplate content type
    let filters = { 
      "content.type": "MailTemplate" 
    }
    
    if (selectedCategory && selectedCategory.length > 0) {
      if (selectedCategory.length === 1) {
        filters["content.category"] = selectedCategory[0].value
      } else {
        filters["content.category"] = { $in: selectedCategory.map(cat => cat.value) }
      }
    }
    return filters
  }, [selectedCategory])

  // Handle category change and trigger refresh
  const handleCategoryChange = (newSelection) => {
    setSelectedCategory(newSelection)
    setRefreshKey(prev => prev + 1)
  }

  // Validators for MailTemplate content
  const validators = (errors) => ({
    data: (value, item) => {
      // Check if data (variables) array exists and has items
      if (!Array.isArray(value) || value.length === 0) {
        return "Variables are required"
      }

      // Check if TITLE variable exists in the data array
      const hasTitleField = value.some(variable =>
        variable && variable.key === "TITLE"
      )

      if (!hasTitleField) {
        return "TITLE field is mandatory in variables"
      }
    }
  })

  return (
    <ContentBrowser
      key={refreshKey}
      localUserPreferencesKey="admin.mailTemplates.browser"
      contentType="MailTemplate"
      contentTitle="Mail Templates"
      addButtonText="Add"
      allowTypeSelection={false}
      allowTypeBrowsing={false}
      showPreview={false}
      searchFields="content.description content.id content.name"
      additionalFilters={dynamicFilters}
      filterDataModel={mailTemplateDataModel}
      customValidators={validators}
      customHeaderControls={
        <div className="flex flex-col -mt-4">
          <Typography variant="admin_txt2_dim" className="mb-0.5">
            Category
          </Typography>

          <DropdownSelect
            variant="admin"
            options={categoryOptionsFromType}
            multiSelection={true}
            searchEnabled={true}
            showCheckbox={false}
            showSelectionTick={true}
            fixedWidth="220px"
            placeholder="All Categories"
            value={selectedCategory}
            onChange={handleCategoryChange}
            onValueItemRender={(option) => {
              // This renders the SELECTED value display (what shows when closed)
              if (option && typeof option === 'object' && option.value) {
                const displayName = categoryDisplayMap[option.value]?.name || option.label || option.value
                return String(displayName)
              }
              return 'All Categories'
            }}
            onItemRender={(option) => {
              // This renders the dropdown ITEMS (what shows when open)
              if (option && typeof option === 'object' && option.value) {
                const categoryData = categoryDisplayMap[option.value]
                const displayName = categoryData?.name || option.label || option.value
                const badgeClass = categoryData?.badge || 'bg-admin_neutral_3 text-white'

                return (
                  <div className="flex items-center space-x-2">
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${badgeClass}`}>
                      {displayName}
                    </span>
                  </div>
                )
              }

              return <span>Invalid option</span>
            }}
          />
        </div>
      }
      styleOverrides={{
        table: "w-full table-fixed",
        container: "bg-admin_paper rounded-3xl p-6 pt-4 overflow-hidden",
        searchInput: "absolute top-[104px] right-8 text-admin_text bg-transparent outline-none flex-1 w-[350px] h-[30px] px-2 py-1"
      }}
      additionalColumns={[
        {
          id: "name",
          title: "Name",
          headerClassName: "w-[25%] min-w-0",
          render: (value, item) => {
            const name = item?.content?.name
            const displayName = typeof name === 'string' ? name : 'N/A'
            return (
              <span className="text-admin_text font-medium truncate block">{displayName}</span>
            )
          },
        },
        {
          id: "description",
          title: "Description",
          headerClassName: "w-[30%] min-w-0",
          render: (value, item) => {
            const description = item?.content?.description
            const displayDescription = typeof description === 'string' ? description : 'N/A'
            
            return (
              <Tooltip 
                content={displayDescription} 
                placement="top"
                display="block"
              >
                <span className="block text-admin_text_secondary cursor-help truncate">
                  {displayDescription}
                </span>
              </Tooltip>
            )
          },
        },
        {
          id: "category",
          title: "Category",
          headerClassName: "w-[12%] min-w-0",
          render: (value, item) => {
            const rawCategory = item?.content?.category
            const category = typeof rawCategory === 'string' ? rawCategory : (rawCategory?.value || rawCategory?.name || 'system')
            const categoryData = categoryDisplayMap[category]
            const badgeClass = categoryData?.badge || 'bg-admin_neutral_3 text-white'
            const displayName = categoryData?.name || category

            return (
              <span className={`px-2 py-0.5 rounded text-xs font-medium block text-center ${badgeClass}`}>
                {displayName}
              </span>
            )
          },
        },
        {
          id: "id",
          title: "ID",
          headerClassName: "w-[18%] min-w-0",
          render: (value, item) => {
            const id = item?.content?.id
            const displayId = typeof id === 'string' ? id : 'N/A'
            
            if (displayId === 'N/A') {
              return (
                <span className="block text-admin_text_secondary font-mono text-xs truncate">
                  {displayId}
                </span>
              )
            }
            
            return (
              <Tooltip 
                content="Click to copy ID" 
                placement="top"
                display="block"
              >
                <span 
                  className="block text-admin_text font-mono text-xs cursor-pointer hover:text-admin_primary hover:bg-admin_neutral_1 px-1 py-0.5 rounded transition-colors truncate"
                  onClick={() => copyToClipboard(displayId, "Template ID")}
                >
                  {displayId}
                </span>
              </Tooltip>
            )
          },
        },
      ]}
    />
  )
}
